import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  limit} from 'firebase/firestore';
import { db } from './firebase';

/**
 * Notification interface based on optimized schema
 */
export interface Notification {
  id: string;

  // userId removed - implicit in subcollection path for better performance

  // Notification content
  title: string;
  message: string;
  type: 'booking' | 'payment' | 'review' | 'meeting_reminder' | 'system';
  actionUrl?: string;

  // Status
  read: boolean;
  readAt?: Timestamp;

  // Context
  relatedId?: string; // ID of related meeting, payment, etc.
  relatedType?: 'meeting' | 'payment' | 'review' | 'booking';

  // Display helpers
  icon?: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';

  // Metadata
  createdAt: Timestamp;
  expiresAt?: Timestamp;
}

export interface CreateNotificationData {
  userId: string;
  title: string;
  message: string;
  type: Notification['type'];
  actionUrl?: string;
  relatedId?: string;
  relatedType?: Notification['relatedType'];
  expiresAt?: Date;
}

/**
 * Gets notifications for a user
 */
export const getUserNotifications = async (
  userId: string,
  limitCount: number = 50,
  includeRead: boolean = true
): Promise<Notification[]> => {
  try {

    const notificationsRef = collection(db, `userNotifications/${userId}/notifications`);

    let q = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    // Filter out read notifications if requested
    if (!includeRead) {
      q = query(
        notificationsRef,
        where('read', '==', false),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );
    }

    const snapshot = await getDocs(q);
    const notifications: Notification[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();

      // Skip expired notifications
      if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
        return;
      }

      notifications.push({
        id: doc.id,
        ...data
      } as Notification);
    });

    return notifications;

  } catch (error) {
    console.error('❌ Error getting user notifications:', error);
    return [];
  }
};

/**
 * Gets unread notifications count
 */
export const getUnreadNotificationCount = async (userId: string): Promise<number> => {
  try {
    const notificationsRef = collection(db, `userNotifications/${userId}/notifications`);
    const q = query(
      notificationsRef,
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);

    // Filter out expired notifications
    const validNotifications = snapshot.docs.filter(doc => {
      const data = doc.data();
      return !data.expiresAt || data.expiresAt.toDate() > new Date();
    });

    return validNotifications.length;
  } catch (error) {
    console.error('Error getting unread notification count:', error);
    return 0;
  }
};

/**
 * Creates a new notification
 */
export const createNotification = async (
  notificationData: CreateNotificationData
): Promise<string | null> => {
  try {

    const notification: Omit<Notification, 'id'> = {
      // userId removed - implicit in subcollection path
      title: notificationData.title,
      message: notificationData.message,
      type: notificationData.type,
      actionUrl: notificationData.actionUrl,
      read: false,
      relatedId: notificationData.relatedId,
      relatedType: notificationData.relatedType,
      icon: getNotificationIcon(notificationData.type),
      priority: 'normal',
      createdAt: Timestamp.now(),
      expiresAt: notificationData.expiresAt ? Timestamp.fromDate(notificationData.expiresAt) : undefined
    };

    const notificationsRef = collection(db, `userNotifications/${notificationData.userId}/notifications`);
    const docRef = await addDoc(notificationsRef, notification);

    return docRef.id;

  } catch (error) {
    console.error('❌ Error creating notification:', error);
    return null;
  }
};

/**
 * Marks a notification as read
 */
export const markNotificationAsRead = async (
  userId: string,
  notificationId: string
): Promise<boolean> => {
  try {
    const notificationRef = doc(db, `userNotifications/${userId}/notifications`, notificationId);

    await updateDoc(notificationRef, {
      read: true,
      readAt: Timestamp.now()
    });

    return true;

  } catch (error) {
    console.error('❌ Error marking notification as read:', error);
    return false;
  }
};

/**
 * Marks multiple notifications as read
 */
export const markNotificationsAsRead = async (
  userId: string,
  notificationIds: string[]
): Promise<number> => {
  try {
    const updatePromises = notificationIds.map(id =>
      markNotificationAsRead(userId, id)
    );

    const results = await Promise.allSettled(updatePromises);
    const successCount = results.filter(result => result.status === 'fulfilled').length;

    return successCount;

  } catch (error) {
    console.error('❌ Error marking notifications as read:', error);
    return 0;
  }
};

/**
 * Marks all notifications as read for a user
 */
export const markAllNotificationsAsRead = async (userId: string): Promise<number> => {
  try {
    // Get all unread notifications
    const notificationsRef = collection(db, `userNotifications/${userId}/notifications`);
    const q = query(
      notificationsRef,
      where('read', '==', false)
    );

    const snapshot = await getDocs(q);
    const notificationIds = snapshot.docs.map(doc => doc.id);

    if (notificationIds.length === 0) {
      return 0;
    }

    return markNotificationsAsRead(userId, notificationIds);

  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    return 0;
  }
};

/**
 * Gets notifications by type
 */
export const getNotificationsByType = async (
  userId: string,
  type: Notification['type'],
  limitCount: number = 20
): Promise<Notification[]> => {
  try {
    const notificationsRef = collection(db, `userNotifications/${userId}/notifications`);
    const q = query(
      notificationsRef,
      where('type', '==', type),
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(q);
    const notifications: Notification[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();

      // Skip expired notifications
      if (data.expiresAt && data.expiresAt.toDate() < new Date()) {
        return;
      }

      notifications.push({
        id: doc.id,
        ...data
      } as Notification);
    });

    return notifications;
  } catch (error) {
    console.error('Error getting notifications by type:', error);
    return [];
  }
};

/**
 * Creates meeting reminder notifications
 */
export const createMeetingReminder = async (
  meetingId: string,
  clientId: string,
  professionalId: string,
  meetingTitle: string,
  meetingDate: string,
  meetingTime: string
): Promise<void> => {
  try {
    const reminderMessage = `Your meeting "${meetingTitle}" is scheduled for ${meetingDate} at ${meetingTime}`;

    // Create reminder for client
    await createNotification({
      userId: clientId,
      title: 'Meeting Reminder',
      message: reminderMessage,
      type: 'meeting_reminder',
      actionUrl: `/meetings/${meetingId}`,
      relatedId: meetingId,
      relatedType: 'meeting'
    });

    // Create reminder for professional
    await createNotification({
      userId: professionalId,
      title: 'Meeting Reminder',
      message: reminderMessage,
      type: 'meeting_reminder',
      actionUrl: `/meetings/${meetingId}`,
      relatedId: meetingId,
      relatedType: 'meeting'
    });

  } catch (error) {
    console.error('❌ Error creating meeting reminders:', error);
  }
};

/**
 * Creates payment confirmation notification
 */
export const createPaymentNotification = async (
  userId: string,
  paymentId: string,
  amount: number,
  currency: string,
  isClient: boolean = true
): Promise<void> => {
  try {
    const formattedAmount = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount / 100);

    const title = isClient ? 'Payment Confirmed' : 'Payment Received';
    const message = isClient
      ? `Your payment of ${formattedAmount} has been processed successfully`
      : `You received a payment of ${formattedAmount}`;

    await createNotification({
      userId,
      title,
      message,
      type: 'payment',
      actionUrl: `/payments/${paymentId}`,
      relatedId: paymentId,
      relatedType: 'payment'
    });

  } catch (error) {
    console.error('❌ Error creating payment notification:', error);
  }
};

/**
 * Creates review request notification
 */
export const createReviewRequestNotification = async (
  userId: string,
  meetingId: string,
  otherParticipantName: string
): Promise<void> => {
  try {
    await createNotification({
      userId,
      title: 'Leave a Review',
      message: `How was your session with ${otherParticipantName}? Your feedback helps others.`,
      type: 'review',
      actionUrl: `/meetings/${meetingId}/review`,
      relatedId: meetingId,
      relatedType: 'meeting',
      expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // Expires in 30 days
    });

  } catch (error) {
    console.error('❌ Error creating review request notification:', error);
  }
};

/**
 * Creates notification when someone receives a new review
 */
export const createNewReviewNotification = async (
  revieweeId: string,
  reviewerName: string,
  rating: number,
  reviewId: string
): Promise<void> => {
  try {
    const stars = '⭐'.repeat(rating);
    await createNotification({
      userId: revieweeId,
      title: 'New Review Received',
      message: `${reviewerName} left you a ${rating}-star review! ${stars}`,
      type: 'review',
      actionUrl: `/profile/reviews`,
      relatedId: reviewId,
      relatedType: 'review'
    });

  } catch (error) {
    console.error('❌ Error creating new review notification:', error);
  }
};

/**
 * Formats notification time for display with timezone awareness
 */
export const formatNotificationTime = (timestamp: Timestamp, userTimezone?: string): string => {
  const now = new Date();
  const notificationTime = timestamp.toDate();
  const diffMs = now.getTime() - notificationTime.getTime();

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMinutes < 1) return 'Just now';
  if (diffMinutes < 60) return `${diffMinutes}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  // Use user's timezone for date formatting if available
  const timeZone = userTimezone && userTimezone !== 'Select timezone' ? userTimezone : undefined;

  return notificationTime.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone
  });
};

/**
 * Gets notification icon based on type
 */
export const getNotificationIcon = (type: Notification['type']): string => {
  const iconMap: Record<Notification['type'], string> = {
    booking: '📅',
    payment: '💰',
    review: '⭐',
    meeting_reminder: '⏰',
    system: '🔔'
  };

  return iconMap[type] || '🔔';
};